"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateOrgRequest,
  OrgActivityQuery,
  UpdateOrgRequest,
  SubscribeRequest,
  BuyTopupRequest,
  RedeemGiftCodeRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";
import {
  putToSignedUrl,
  makeAggregateProgress,
  type UploadProgress,
} from "../upload";

type DocFile = {
  file: File;
  contentType: "image/jpeg" | "image/png" | "application/pdf";
};

export type SubmitOrgKycInput = {
  legalName: string;
  taxCode: string;
  representativeName: string;
  businessType: string;
  registrationDoc: DocFile;
  taxDoc?: DocFile;
  // Overall upload percent (0–100) across the doc(s), byte-weighted.
  onProgress?: UploadProgress;
};

export function useOrg(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.detail(orgId ?? ""),
    queryFn: async () => getSdk().orgs.get(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

// Subscription plan status (effective plan, remaining sourcing/boost incl.
// extra, days left) — drives the credit/plan UI across the employer app.
export function usePlanStatus(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.planStatus(orgId ?? ""),
    queryFn: async () => getSdk().orgs.getPlanStatus(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

// Buyable plan catalog (active, non-FREE plans with live prices + optional
// struck-through originalPriceVnd) for the upgrade modal. Prices come from the
// DB so admin edits show up without a redeploy.
export function usePlanCatalog(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.planCatalog(orgId ?? ""),
    queryFn: async () => getSdk().orgs.getPlanCatalog(orgId!),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

// Purchase history (every order, any status) for the billing screen.
export function usePlanPurchaseHistory(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.planPurchases(orgId ?? ""),
    queryFn: async () => getSdk().orgs.listPlanPurchases(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

// One order's full detail for the invoice/result screen (/billing/:orderCode).
// Polls every 5s while the order is still PENDING so a webhook-settled order
// flips to PAID on screen without a manual refresh; stops polling once settled.
export function usePlanPurchaseDetail(
  orgId: string | null | undefined,
  orderCode: string | null | undefined,
) {
  return useQuery({
    queryKey: qk.orgs.planPurchase(orgId ?? "", orderCode ?? ""),
    queryFn: async () => getSdk().orgs.getPlanPurchase(orgId!, orderCode!),
    enabled: !!orgId && !!orderCode,
    refetchInterval: (query) =>
      query.state.data?.status === "PENDING" ? 5000 : false,
  });
}

// Start a plan purchase (returns PENDING order + Sepay handoff / simulated).
export function useSubscribePlan(orgId: string) {
  return useMutation({
    mutationFn: (body: SubscribeRequest) =>
      getSdk().orgs.subscribePlan(orgId, body),
  });
}

// Buy an à-la-carte top-up (extra sourcing/boost).
export function useBuyTopup(orgId: string) {
  return useMutation({
    mutationFn: (body: BuyTopupRequest) => getSdk().orgs.buyTopup(orgId, body),
  });
}

// Fetch the VietQR bank-transfer QR for a PENDING plan/topup order. `orderCode`
// is the providerOrderId (PLAN-<seq>) from the created purchase.
export function usePlanPurchaseQr(orgId: string) {
  return useMutation({
    mutationFn: (orderCode: string) =>
      getSdk().orgs.getPlanPurchaseQr(orgId, orderCode),
  });
}

// Nudge Casso to sync after the buyer confirms payment, so the settling webhook
// fires sooner. Best-effort — the order still settles via the webhook.
export function useSyncPlanPurchase(orgId: string) {
  return useMutation({
    mutationFn: (orderCode: string) =>
      getSdk().orgs.syncPlanPurchase(orgId, orderCode),
  });
}

// Settle a simulated purchase (dev) then refresh plan status.
export function useConfirmPlanPurchase(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseId: string) =>
      getSdk().orgs.confirmPlanPurchase(orgId, purchaseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgs.planStatus(orgId) });
      // Upgrading changes the posting quota (maxConcurrentPostings), so refresh
      // insights too — otherwise the postings page keeps the old "at limit"
      // state and the "Đăng ca mới" button wrongly opens the upgrade modal.
      qc.invalidateQueries({ queryKey: qk.orgs.insights(orgId) });
      // The settled order now shows as PAID in the billing history.
      qc.invalidateQueries({ queryKey: qk.orgs.planPurchases(orgId) });
    },
  });
}

// Redeem a plan gift code ("mã quà tặng") then refresh plan-derived state. Same
// invalidations as a settled purchase — a gift grant changes the plan/quota too.
export function useRedeemGiftCode(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RedeemGiftCodeRequest) =>
      getSdk().orgs.redeemGiftCode(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgs.planStatus(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.insights(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.planPurchases(orgId) });
    },
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrgRequest) => getSdk().orgs.create(body),
    // Awaiting the `me` refetch is load-bearing: the AuthGate on /dashboard
    // reads `memberships` from the `me` cache, so it must reflect the new org
    // before we navigate — otherwise it bounces back to onboarding.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.me });
      qc.invalidateQueries({ queryKey: qk.orgs.mine });
    },
  });
}

// Picks the R2-friendly content type from a browser File. Gallery uploads
// accept the same set as avatars (JPEG/PNG/WebP); anything else is rejected
// before we bother presigning.
function galleryContentType(
  file: File,
): "image/jpeg" | "image/png" | "image/webp" {
  if (file.type === "image/png") return "image/png";
  if (file.type === "image/webp") return "image/webp";
  if (file.type === "image/jpeg") return "image/jpeg";
  throw new Error("unsupported_image_type");
}

// Variables for the single-image upload hooks. `onProgress` (0–100) drives the
// percent UI in LogoUploader / ImageGalleryEditor; omit it to upload silently.
export type ImageUploadVars = { file: File; onProgress?: UploadProgress };

// Upload a single org gallery image: presign → PUT to R2 → return the new
// object key + public URL. The caller collects keys and persists the ordered
// set via `useUpdateOrg({ galleryImageKeys })`. Employer (own org) flow.
export function useUploadOrgGalleryImage(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().orgs.presignImage(orgId, {
        contentType,
        sizeBytes: file.size,
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

// Same as `useUploadOrgGalleryImage` but routed through the admin endpoint so
// staff can add photos to any org. Pairs with `admin.updateOrg`.
export function useUploadAdminOrgGalleryImage(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().admin.presignOrgImage(orgId, {
        contentType,
        sizeBytes: file.size,
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

// Upload (or replace) the org logo ("avatar"): presign with purpose "logo" →
// PUT to R2 → return the new key + public URL. The caller persists it via
// `useUpdateOrg({ logoImageKey })`. Employer (own org) flow.
export function useUploadOrgLogo(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().orgs.presignImage(orgId, {
        contentType,
        sizeBytes: file.size,
        purpose: "logo",
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

// Same as `useUploadOrgLogo` but routed through the admin endpoint so staff
// can set the logo for any org. Pairs with `admin.updateOrg({ logoImageKey })`.
export function useUploadAdminOrgLogo(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().admin.presignOrgImage(orgId, {
        contentType,
        sizeBytes: file.size,
        purpose: "logo",
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

// Upload (or replace) a posting's square preview image: presign with purpose
// "posting" → PUT to R2 → return the new key + public URL. The posting form
// collects the key and submits it via create/update posting. Employer flow
// (own org). orgId scopes the R2 key namespace, mirroring logo/gallery.
export function useUploadPostingImage(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().orgs.presignImage(orgId, {
        contentType,
        sizeBytes: file.size,
        purpose: "posting",
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

// Same as `useUploadPostingImage` but routed through the admin endpoint so
// staff can set the preview image when posting on behalf of any org.
export function useUploadAdminPostingImage(orgId: string) {
  return useMutation({
    mutationFn: async ({ file, onProgress }: ImageUploadVars) => {
      const contentType = galleryContentType(file);
      const presigned = await getSdk().admin.presignOrgImage(orgId, {
        contentType,
        sizeBytes: file.size,
        purpose: "posting",
      });
      await putToSignedUrl(presigned.uploadUrl, file, contentType, onProgress);
      return { key: presigned.key, publicUrl: presigned.publicUrl };
    },
  });
}

export function useUpdateOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgRequest) =>
      getSdk().orgs.update(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

// Weekly stats + credit balance + actionable tips for the employer
// "Ca của tôi" right rail. Tips include posting-scoped nudges (FE matches
// by `postingId` and renders inline under the matching card).
export function useOrgInsights(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.insights(orgId ?? ""),
    queryFn: async () => getSdk().orgs.getInsights(orgId!),
    enabled: !!orgId,
    // Tips can lag a minute behind reality without harm — saves recomputes
    // when the user toggles filter chips on the same page.
    staleTime: 60_000,
  });
}

// Org-scoped audit feed. OWNER + HR only — server returns 403 for other roles.
export function useOrgActivity(
  orgId: string | null | undefined,
  query: OrgActivityQuery = {},
) {
  return useQuery({
    queryKey: [...qk.orgs.activity(orgId ?? ""), query] as const,
    queryFn: async () => getSdk().orgs.listActivity(orgId!, query),
    enabled: !!orgId,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useOrgKyc(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.orgs.kyc(orgId ?? ""),
    queryFn: async () => getSdk().kyc.getOrgKyc(orgId!),
    enabled: !!orgId,
  });
}

export function useSubmitOrgKyc(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SubmitOrgKycInput) => {
      const sdk = getSdk();
      const presigned = await sdk.kyc.presignOrgKyc(orgId, {
        registrationDoc: {
          contentType: input.registrationDoc.contentType,
          sizeBytes: input.registrationDoc.file.size,
        },
        ...(input.taxDoc
          ? {
              taxDoc: {
                contentType: input.taxDoc.contentType,
                sizeBytes: input.taxDoc.file.size,
              },
            }
          : {}),
      });

      // Byte-weighted aggregate percent across reg doc (+ optional tax doc).
      const sizes = [
        input.registrationDoc.file.size,
        input.taxDoc?.file.size ?? 0,
      ];
      const track = makeAggregateProgress(sizes, input.onProgress);

      await putToSignedUrl(
        presigned.registrationDoc.uploadUrl,
        input.registrationDoc.file,
        input.registrationDoc.contentType,
        track(0),
      );
      if (input.taxDoc && presigned.taxDoc) {
        await putToSignedUrl(
          presigned.taxDoc.uploadUrl,
          input.taxDoc.file,
          input.taxDoc.contentType,
          track(1),
        );
      }

      const res = await sdk.kyc.submitOrgKyc(orgId, {
        legalName: input.legalName,
        taxCode: input.taxCode,
        representativeName: input.representativeName,
        businessType: input.businessType,
        registrationDocKey: presigned.registrationDoc.key,
        taxDocKey:
          input.taxDoc && presigned.taxDoc ? presigned.taxDoc.key : undefined,
        consentAccepted: true,
      });
      return res.submission;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgs.kyc(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
